<template>
  <div
    v-if="productsWithDelivery.length > 0"
    class="flex h-full w-full flex-col items-center justify-start gap-y-6 rounded-md bg-white p-10 shadow-lg sm:p-14"
  >
    <div class="flex h-full w-full flex-col items-center justify-start gap-y-2">
      <span class="w-full text-3xl font-medium"
        >{{ __('Delivery Products') }}
        {{ '(' + productsWithDelivery.length + ')' }}
      </span>

      <span class="w-full" v-if="data.shipping_details.has_shipping_details_set"
        >{{
          __(
            'Here’s your delivery information from your account. You only need to confirm the order. If you want to receive the package somewhere else, you can change the shipping address.'
          )
        }}
      </span>
      <span
        class="w-full"
        v-else-if="!data.shipping_details.has_shipping_details_set"
        >{{
          __(
            'Your account currently has no delivery information. Please update it here.'
          )
        }}
      </span>

      <div
        v-if="data.product_variants.length > 0"
        class="flex w-full flex-row items-center justify-start gap-x-4 pt-2"
      >
        <div class="flex flex-row items-start justify-start gap-x-4">
          <div
            v-for="(product_variant, index) in productsWithDelivery"
            :key="product_variant.variant.id"
            class="flex h-24 w-24 flex-col items-stretch justify-center gap-y-1"
          >
            <div class="flex flex-col items-center justify-center space-y-2">
              <div
                @click.prevent="
                  redirectToPage(
                    'redesign.candidate.shop.products.show',
                    product_variant.variant.product.id
                  )
                "
                class="aspect-square cursor-pointer overflow-hidden rounded-lg shadow-md transition-all duration-200 hover:shadow-lg"
              >
                <img
                  :src="`/uploads/${product_variant.variant.primary_image.path}`"
                  :key="product_variant.variant.primary_image.id"
                  :alt="__('Media image')"
                  class="h-24 w-24 rounded-md border border-gray-200 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
      <Input
        v-model="data.shipping_details.first_name"
        :value="data.shipping_details.first_name"
        :placeholder="__('John')"
        :label="__('First name')"
        type="text"
        onkeydown=""
        class="w-full"
        :errors="errors?.first_name"
        required
      />
      <Input
        v-model="data.shipping_details.last_name"
        :value="data.shipping_details.last_name"
        :placeholder="__('Smith')"
        :label="__('Last name')"
        type="text"
        onkeydown=""
        class="w-full"
        :errors="errors?.last_name"
        required
      />

      <Input
        v-model="data.shipping_details.address"
        :value="data.shipping_details.address"
        :placeholder="__('Enter address')"
        :label="__('Adress')"
        type="text"
        onkeydown=""
        class="w-full sm:col-span-2"
        :errors="errors?.address"
        required
      />

      <Input
        v-model="data.shipping_details.detailed_address"
        :value="data.shipping_details.detailed_address"
        :placeholder="__('Add adress details')"
        :label="__('Adress Details')"
        type="text"
        onkeydown=""
        class="w-full sm:col-span-2"
        :errors="errors?.detailed_address"
      />

      <InputLocation
        id="location"
        v-model:value="data.shipping_details.location"
        :errors="errors?.location"
        :label-country="__('Country')"
        :label-city="__('City')"
        :placeholder-country="__('Select country')"
        :placeholder-city="__('Select city')"
        :options="data.options.countries"
        class="md:col-span-2"
        disabled
      />

      <Input
        v-model="data.shipping_details.postal_code"
        :value="data.shipping_details.postal_code"
        :placeholder="__('061254')"
        :label="__('Postal code')"
        type="text"
        onkeydown=""
        class="w-full sm:col-span-2"
        :errors="errors?.postal_code"
      />

      <InputEmail
        id="email"
        v-model:value="data.shipping_details.email"
        :errors="errors?.email"
        :label="__('Email')"
        :placeholder="__('Your email')"
      />
      <InputPhone
        v-model:value-phone-prefix="data.shipping_details.country_prefix"
        v-model:value-phone-number="data.shipping_details.phone"
        v-model:errors-phone-prefix="phonePrefixErrors"
        v-model:errors-phone-number="phoneNumberErrors"
        :options="data.options.phone_codes"
        class=""
      />
    </div>
    <div
      v-if="data.shipping_details.has_shipping_details_set"
      class="flex w-full items-center justify-center gap-x-2 sm:justify-start"
    >
      <input
        type="checkbox"
        class="size-5 rounded-md border-gray-300 text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
        :checked="updateAddress"
        @change="handleUpdateAddress"
      />
      <span class="text-zinc-600">
        {{
          __(
            'Update my account information to reflect this address as the primary.'
          )
        }}</span
      >
    </div>
  </div>

  <div
    v-for="productsWithPickup in groupOfProductsWithPickup"
    class="flex h-full w-full flex-col items-center justify-start gap-y-6 rounded-md bg-white p-10 shadow-lg sm:p-14"
  >
    <div class="flex w-full flex-col items-start justify-center gap-y-2 py-2">
      <span class="text-3xl font-medium"
        >{{ __('Pickup Products') }}
        {{ '(' + productsWithPickup.length + ')' }}</span
      >

      <div class="flex flex-col gap-y-1 pt-1 text-zinc-600">
        <span>
          {{
            __(
              "You'll receive an email/ message on the platform with all the details."
            )
          }}
        </span>

        <span>
          <strong>{{ __('Pickup Address:') }}</strong>
          {{
            productsWithPickup.length > 0
              ? productsWithPickup[0].variant.product.pickup_address
              : ''
          }}
        </span>
      </div>

      <div
        v-if="data.product_variants.length > 0"
        class="flex w-full flex-col items-center justify-start gap-x-4 pt-2"
      >
        <div class="flex w-full flex-row items-start justify-start gap-x-4">
          <div
            v-for="(product_variant, index) in productsWithPickup"
            :key="product_variant.variant.id"
            class="flex h-24 w-24 flex-col items-stretch justify-center gap-y-1"
          >
            <div class="flex flex-col items-center justify-center space-y-2">
              <div
                @click.prevent="
                  redirectToPage(
                    'redesign.candidate.shop.products.show',
                    product_variant.variant.product.id
                  )
                "
                class="aspect-square cursor-pointer overflow-hidden rounded-lg shadow-md transition-all duration-200 hover:shadow-lg"
              >
                <img
                  :src="`/uploads/${product_variant.variant.primary_image.path}`"
                  :key="product_variant.variant.primary_image.id"
                  :alt="__('Media image')"
                  class="h-24 w-24 rounded-md border border-gray-200 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="onlineProducts.length > 0"
    class="flex h-full w-full flex-col items-center justify-start gap-y-6 rounded-md bg-white p-10 shadow-lg sm:p-14"
  >
    <div class="flex w-full flex-col items-start justify-center gap-y-2 py-2">
      <div class="flex w-full items-center justify-between gap-x-4">
        <div class="text-3xl font-medium">
          {{ __('Online Products') }} {{ '(' + onlineProducts.length + ')' }}
        </div>
      </div>
      <div class="flex flex-col gap-y-1 pt-1 text-zinc-600">
        <span>
          {{
            __(
              "You'll receive an email/ message on the platform with all the details."
            )
          }}
        </span>
      </div>

      <div
        v-if="data.product_variants.length > 0"
        class="flex w-full flex-col items-center justify-start gap-x-4 pt-2"
      >
        <div class="flex w-full flex-row items-start justify-start gap-x-4">
          <div
            v-for="(product_variant, index) in onlineProducts"
            :key="product_variant.variant.id"
            class="flex h-24 w-24 flex-col items-stretch justify-center gap-y-1"
          >
            <div class="flex flex-col items-center justify-center space-y-2">
              <div
                @click.prevent="
                  redirectToPage(
                    'redesign.candidate.shop.products.show',
                    product_variant.variant.product.id
                  )
                "
                class="aspect-square cursor-pointer overflow-hidden rounded-lg shadow-md transition-all duration-200 hover:shadow-lg"
              >
                <img
                  :src="`/uploads/${product_variant.variant.primary_image.path}`"
                  :key="product_variant.variant.primary_image.id"
                  :alt="__('Media image')"
                  class="h-24 w-24 rounded-md border border-gray-200 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    class="flex w-full items-center justify-between gap-x-2 rounded-md bg-white p-4 shadow-lg"
  >
    <div class="flex w-fit items-center justify-start sm:w-full">
      <button
        @click="$emit('cancel')"
        class="btn hover-lift-no-shadow flex items-center justify-center gap-x-2"
      >
        <Icon
          name="arrow_back"
          type="filled"
          class="text-xl text-zinc-800"
        ></Icon>
        <span class="hidden font-medium text-zinc-800 sm:block">{{
          __('Go Back')
        }}</span>
      </button>
    </div>
    <div class="flex w-full flex-row items-center justify-end gap-x-2">
      <button
        @click="$emit('cancel')"
        class="btn btn-ghost hover-lift-no-shadow"
      >
        <span class="text-primary">
          {{ __('Cancel') }}
        </span>
      </button>
      <button
        @click="$emit('handle-validate-shipping')"
        class="btn btn-fill hover-lift-no-shadow"
      >
        <span>
          {{ __('Next') }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup>
  import { ref } from 'vue';
  import InputEmail from '@/components/form/input-email/input-email.vue';
  import InputLocation from '@/components/form/input-location/input-location.vue';
  import InputPhone from '@/components/form/input-phone/input-phone.vue';
  import Input from '@/components/global/Form/Input.vue';
  import Icon from '@/components/block/icon/icon.vue';
  import { redirectToPage } from '@/helpers';

  const props = defineProps({
    data: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      required: false
    },
    updateAddress: {
      type: Boolean,
      required: false,
      default: false
    },
    shippingStep: {
      type: Number,
      required: false,
      default: 1
    }
  });

  const data = ref(props.data);
  const emit = defineEmits(['cancel', 'next']);

  const getPickupProductsGrouped = () => {
    const pickupProducts = data.value.product_variants.filter(
      (product_variant) => product_variant.shipping_type === 'Pickup'
    );
    const grouped = pickupProducts.reduce((acc, product) => {
      const product_pickup_address = product.variant.product.pickup_address;
      if (!acc[product_pickup_address]) {
        acc[product_pickup_address] = [];
      }
      acc[product_pickup_address].push(product);
      return acc;
    }, {});

    return Object.values(grouped);
  };

  const productsWithDelivery = ref(
    data.value.product_variants.filter(
      (product_variant) => product_variant.shipping_type === 'Delivery'
    )
  );

  const groupOfProductsWithPickup = ref(getPickupProductsGrouped());
  const onlineProducts = ref(
    data.value.product_variants.filter(
      (product_variant) => product_variant.shipping_type === 'Online'
    )
  );

  const updateAddress = defineModel('updateAddress');

  const handleUpdateAddress = () => {
    updateAddress.value = true;
  };
</script>
